import React, { useEffect, useRef, useState, useContext } from "react";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { productUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import ListRow from "../../profiles/ListRow";
import CompanyPersonRowSkeleton from "./company_person_row_skeleton";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";
import { CompanyContext } from "../data/company_context_management";

const PER_PAGE = 12;
const FIRST_PAGE_PLACEHOLDERS = 6;
const NEXT_PAGE_PLACEHOLDERS = 3;

// The dividers belong to the list, not the row: every row but the last carries the rule beneath it.
function dividerClasses(index, count) {
  return `py-2${index < count - 1 ? " border-bottom" : ""}`;
}

// A PR firm's own "Products" section: the Products it represents, self-contained like
// its Experts/PR Team/Brands siblings so it collapses away entirely when there is nothing to show.
// Renders on the pr_firm subtype only — the underlying representations are the same ones the firm's
// "Our Experts" card queries for the Expert axis, just filtered to the Product axis instead.
function CompanyProducts() {
  const { companyId, company, viewerContext } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const configured = company.displayType === "pr_firm";

  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [asyncError, setAsyncError] = useState();

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  const consumedTriggerRef = useRef(0);
  useEffect(() => {
    if (lastTriggeredAt <= consumedTriggerRef.current) return;

    consumedTriggerRef.current = lastTriggeredAt;
    if (hasMore) setPage((prevPage) => prevPage + 1);
  }, [lastTriggeredAt, hasMore]);

  useEffect(() => {
    let componentIsMounted = true;

    const fetchProducts = async () => {
      try {
        if (companyId && configured) {
          if (page === 1) setIsLoading(true);
          qahoy.track("Companies#getRepresentedProducts");

          const response = await qapi.representations.getProductsForPromoterCompany(companyId, {
            page,
            per_page: PER_PAGE,
          });

          if (componentIsMounted) {
            if (response.data) {
              const results = response.data;
              if (results && results.length > 0) {
                // The view fans a row out per representation job, so the same product can arrive
                // more than once across (or even within) a page — dedupe by promoted_entity_id as
                // pages accumulate rather than trusting one page to already be unique.
                setProducts((prevRecords) => {
                  const seen = new Set(prevRecords.map((product) => product.id));
                  const newProducts = [];
                  results.forEach((item) => {
                    if (item.promoted_entity_id == null || seen.has(item.promoted_entity_id)) return;
                    seen.add(item.promoted_entity_id);
                    newProducts.push({
                      id: item.promoted_entity_id,
                      name: item.promoted_entity_name,
                      profile_image_url: item.promoted_entity_profile_image_url,
                      slug: item.promoted_entity_slug,
                      migrate_id: item.promoted_entity_migrate_id,
                    });
                  });
                  return [...prevRecords, ...newProducts];
                });
              }
              // `response.remainingRows` is PostgREST's ESTIMATED count and can under-report, ending
              // the scroll before every row has been seen. A full page proves nothing either way and
              // always earns another request; only a SHORT page (fewer than PER_PAGE rows) proves
              // there is no more data.
              setHasMore(results.length === PER_PAGE);
            } else if (response.error) {
              throw new Error(JSON.stringify(response.error));
            }
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (componentIsMounted) setAsyncError(error);
      }
    };
    fetchProducts();

    return () => {
      componentIsMounted = false;
    };
  }, [companyId, configured, page]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  if (!configured) return null;

  const isEmpty = !isLoading && products.length === 0;
  if (isEmpty && !editable) return null;

  return (
    <SectionCard id="company-section-products" title="Our Products">
      {isEmpty ? (
        <div className="text-muted">No products added yet.</div>
      ) : (
        <div className="col px-0">
          <ul className="list-unstyled mb-0">
            {isLoading &&
              Array.from({ length: FIRST_PAGE_PLACEHOLDERS }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li className={dividerClasses(index, FIRST_PAGE_PLACEHOLDERS)} key={`product-placeholder-${index}`}>
                  <CompanyPersonRowSkeleton />
                </li>
              ))}
            {!isLoading &&
              products.map((product, index) => (
                <li className={dividerClasses(index, products.length)} key={product.id}>
                  <ListRow
                    href={productUrl({ slug: product.slug, migrate_id: product.migrate_id })}
                    imageUrl={product.profile_image_url}
                    title={product.name}
                  />
                </li>
              ))}
          </ul>
          {hasMore && (
            <div ref={scrollTriggerRef}>
              <ul className="list-unstyled mb-0">
                {Array.from({ length: NEXT_PAGE_PLACEHOLDERS }).map((_, index) => (
                  <li
                    className={dividerClasses(index, NEXT_PAGE_PLACEHOLDERS)}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`product-next-placeholder-${index}`}
                  >
                    <CompanyPersonRowSkeleton />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

export default CompanyProducts;
